import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Clock } from "lucide-react";
import api from "@/lib/api";
import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import TopMarquee from "@/components/site/TopMarquee";
import Footer from "@/components/site/Footer";
import PopupAd from "@/components/site/PopupAd";
import CategoryNav from "@/components/site/CategoryNav";
import { ShareRow } from "@/components/site/Share";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shareUrlFor = (slug) => (slug ? `${BACKEND}/api/share/${slug}` : undefined);

const PAGE = 30;

export default function CategoryPage() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    // राजकाज is an external link (जनसंपर्क MP) — never load internally.
    if (key === "rajkaj") {
      window.location.replace("https://www.mpinfo.org/");
      return;
    }
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    api.get(`/public/category/${key}?limit=${PAGE}`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.category.label} | देनवा पोस्ट`;
      })
      .catch(() => setNotFound(true));
  }, [key]);

  const loadMore = async () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/public/category/${key}?limit=${PAGE}&skip=${data.items.length}`);
      setData({ ...data, items: [...data.items, ...r.data.items] });
    } catch (_) {}
    finally { setLoadingMore(false); }
  };

  if (notFound) {
    return (
      <SmoothScroll>
        <div className="App font-sans-dev">
          <TopMarquee /><Header />
          <main className="min-h-[50vh] flex items-center justify-center px-4 py-24 text-center">
            <div>
              <h1 className="font-serif-dev font-black text-4xl mb-4">यह श्रेणी नहीं मिली</h1>
              <Link to="/" className="pill-btn text-[12px]">होम पर वापस</Link>
            </div>
          </main>
          <Footer />
        </div>
      </SmoothScroll>
    );
  }

  if (!data) {
    return (
      <SmoothScroll>
        <div className="App font-sans-dev">
          <TopMarquee /><Header />
          <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#D92525]" size={36} /></div>
          <Footer />
        </div>
      </SmoothScroll>
    );
  }

  const { category, items, total } = data;
  const lead = items[0];
  const rest = items.slice(1);

  return (
    <SmoothScroll>
      <div className="App font-sans-dev" data-testid={`category-page-${key}`}>
        <TopMarquee />
        <Header />

        {/* Category quick-nav (also lets user jump between categories) */}
        <CategoryNav current={key} />

        <main className="bg-[#F4F4F0]">
          {/* Header */}
          <section className="border-b border-[#0F0F0F] px-4 md:px-8 py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto">
              <Link to="/" className="text-[12px] uppercase tracking-[0.22em] text-[#4A4A4A] hover:text-[#D92525] inline-flex items-center gap-2">
                <ArrowLeft size={14} /> होम पर वापस
              </Link>
              <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                  <div className="eyebrow text-[#D92525] mb-3">{category.label_en}</div>
                  <h1 className="font-serif-dev font-black text-[42px] md:text-[68px] leading-[0.98]">
                    {category.label}
                  </h1>
                </div>
                <div className="font-sans-dev text-[12px] uppercase tracking-[0.22em] text-[#4A4A4A]">
                  {total} ख़बर
                </div>
              </div>
            </div>
          </section>

          {/* Grid */}
          <section className="px-4 md:px-8 py-12 md:py-16">
            <div className="max-w-[1400px] mx-auto">
              {items.length === 0 ? (
                <div className="py-20 text-center text-[#4A4A4A]">इस श्रेणी में अभी कोई ख़बर नहीं।</div>
              ) : (
                <>
                  {/* Lead */}
                  {lead && (
                    <motion.a
                      href={`/news/${lead.slug}`}
                      data-testid={`cat-lead-${lead.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="grid grid-cols-12 gap-6 md:gap-10 mb-14 group"
                    >
                      <div className="col-span-12 lg:col-span-7">
                        <div className="thumb-wrap aspect-[16/10] border border-[#0F0F0F]/15 bg-[#0F0F0F]/6">
                          {lead.img && <img src={lead.img} alt={lead.title} className="w-full h-full object-contain" loading="lazy" />}
                        </div>
                      </div>
                      <div className="col-span-12 lg:col-span-5 flex flex-col justify-center">
                        <div className="eyebrow text-[#D92525] mb-3">{lead.kicker || category.label} · {timeAgo(lead.published_at)}</div>
                        <h2 className="font-serif-dev font-black text-[28px] md:text-[38px] leading-[1.1] headline-link">
                          {lead.title}
                        </h2>
                        {lead.summary && (
                          <p className="mt-4 font-sans-dev text-[15px] md:text-[16px] text-[#4A4A4A] leading-relaxed max-w-[560px]">
                            {lead.summary}
                          </p>
                        )}
                        <div className="mt-4">
                          <ShareRow title={lead.title} url={shareUrlFor(lead.slug)} testid={`share-cat-lead-${lead.id}`} />
                        </div>
                      </div>
                    </motion.a>
                  )}

                  {/* Rest as grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {rest.map((n, i) => (
                      <motion.a
                        key={n.id}
                        href={`/news/${n.slug}`}
                        data-testid={`cat-item-${n.id}`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                        className="group block"
                      >
                        <div className="thumb-wrap aspect-[4/3] mb-4 border border-[#0F0F0F]/15 bg-[#0F0F0F]/6">
                          {n.img && <img src={n.img} alt={n.title} className="w-full h-full object-contain" loading="lazy" />}
                        </div>
                        <div className="eyebrow text-[#D92525] mb-2 flex items-center gap-2">
                          <Clock size={11} /> {timeAgo(n.published_at)}
                        </div>
                        <h3 className="font-serif-dev font-bold text-[18px] leading-snug headline-link">
                          {n.title}
                        </h3>
                        <div className="mt-3 pt-3 border-t border-[#0F0F0F]/10">
                          <ShareRow title={n.title} url={shareUrlFor(n.slug)} testid={`share-cat-${n.id}`} />
                        </div>
                      </motion.a>
                    ))}
                  </div>

                  {/* Load more */}
                  {items.length < total && (
                    <div className="mt-12 text-center">
                      <button
                        onClick={loadMore}
                        disabled={loadingMore}
                        data-testid="cat-load-more"
                        className="pill-btn text-[12px] mx-auto disabled:opacity-60"
                      >
                        {loadingMore ? <><Loader2 size={16} className="animate-spin" /> लोड हो रहा है</> : "और ख़बरें"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </main>

        <PopupAd />
        <Footer />
      </div>
    </SmoothScroll>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return "अभी";
    if (diff < 3600) return `${Math.floor(diff / 60)} मिनट पहले`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} दिन पहले`;
    return new Date(iso).toLocaleDateString("hi-IN", { day: "numeric", month: "short" });
  } catch { return ""; }
}
